import { Flex, FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { themeColors } from "@/utils/themeColors";
import { ChatAgent } from "../types";
import HtmlText from "@/components/HtmlText";
import Mountable from "@/components/styled/div/Mountable";
import { getHtmlFromContent } from "@knowt/editor/helpers/getHtmlFromContent";
import { ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { spacing } from "@/utils/spacing";
import { useFeedbackFlowContextSelector } from "@/components/Feedback/FeedbackFlowContext";
import toast from "react-hot-toast";
import { FeedbackEvents } from "@/components/Feedback/feedbackPopupContent";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { useChatEditorCommunicator } from "../hooks/useChatEditorCommunicator";
import { LocalMessageAttachment } from "@knowt/syncing/types/common";
import RichEditorAttachmentList from "@/components/richEditorResponses/RichEditorAttachmentList";

const ChatText = ({
    text,
    quote,
    type,
    isLoading,
    attachments,
}: {
    text?: string;
    quote?: string;
    type?: ChatAgent;
    isLoading?: boolean;
    attachments?: LocalMessageAttachment[];
}) => {
    const openFeedbackPopup = useFeedbackFlowContextSelector(state => state.openFeedbackPopup);
    const setSubmissions = useFeedbackFlowContextSelector(state => state.setSubmissions);
    const submissions = useFeedbackFlowContextSelector(state => state.submissions);

    const itemId = useChatEditorCommunicator(s => s.itemId);
    const itemType = useChatEditorCommunicator(s => s.itemType);

    return (
        <Flex style={{ gap: "1rem", width: "100%", flexDirection: type === "user" ? "row-reverse" : "row" }}>
            <FlexColumn
                style={{
                    backgroundColor: type === ChatAgent.USER ? themeColors.background : undefined,
                    color: themeColors.neutralBlack,
                    width: "fit-content",
                    paddingInlineStart: "1.6rem",
                    paddingInlineEnd: type === ChatAgent.USER ? "1.6rem" : undefined,
                    paddingBlock: "1.6rem",
                    borderRadius: "2rem",
                    borderBottomLeftRadius: type === "bot" ? "0.5rem" : "2rem",
                    borderBottomRightRadius: type === "user" ? "0.5rem" : "2rem",
                    justifyContent: "space-between",
                    maxWidth: type === ChatAgent.USER ? "70%" : undefined,
                    gap: spacing.XS_2,
                }}>
                <Mountable mount={!!attachments}>
                    <RichEditorAttachmentList localEditorAttachments={attachments} readOnly />
                </Mountable>
                <Mountable mount={!!quote}>
                    <div
                        className="body2"
                        style={{
                            padding: "1.6rem",
                            width: "100%",
                            backgroundColor: themeColors.neutralWhite,
                            color: themeColors.neutralBlack,
                            borderRadius: "2rem",
                            marginBottom: "1rem",
                        }}
                        data-testid="auto-id-083af2e9-f1b9-4ee8-8639-a921a684e66f">
                        {HtmlText({ text: quote })}
                    </div>
                </Mountable>

                <FlexRowAlignCenter style={{ gap: "1rem" }}>
                    {text && (
                        <HtmlText
                            style={{ wordBreak: "break-word", fontFamily: "sans-serif" }}
                            text={getHtmlFromContent({
                                content: text,
                            })}
                        />
                    )}

                    {isLoading && (
                        <div
                            style={{ marginTop: type === ChatAgent.BOT ? "0.5rem" : undefined }}
                            data-testid="auto-id-4e0b2873-13a0-483c-b321-5bf80e8fc1b4">
                            <l-dot-wave speed={1} size={25} color={themeColors.neutralBlack} object-fit="contain" />
                        </div>
                    )}
                </FlexRowAlignCenter>
                {type === ChatAgent.BOT && !isLoading && !submissions[FeedbackEvents.MEDIA_SINGLE_CHAT + text] && (
                    <FlexRowAlignCenter
                        style={{
                            gap: spacing.XS_2,
                        }}>
                        <ThumbsUpIcon
                            size={20}
                            color={themeColors.neutralBlack}
                            style={{
                                cursor: "pointer",
                            }}
                            onClick={async () => {
                                toast.success("Thank you for your feedback!");
                                setSubmissions(prev => ({
                                    ...prev,
                                    [FeedbackEvents.MEDIA_SINGLE_CHAT + text]: 5,
                                }));
                                await Mixpanel.track("Chat AI Feedback Submitted", {
                                    rating: 5,
                                    reason: "",
                                    category: "",
                                    followUp: "",
                                    itemId,
                                    itemType,
                                });
                            }}
                        />
                        <ThumbsDownIcon
                            size={20}
                            color={themeColors.neutralBlack}
                            style={{
                                cursor: "pointer",
                            }}
                            onClick={() => {
                                openFeedbackPopup({
                                    feedbackFor: FeedbackEvents.MEDIA_SINGLE_CHAT,
                                    rating: 1,
                                    onSubmitHandler: async ({ ...props }) => {
                                        await Mixpanel.track("Chat AI Feedback Submitted", {
                                            ...props,
                                            itemId,
                                            itemType,
                                        });
                                        setSubmissions(prev => ({
                                            ...prev,
                                            [FeedbackEvents.MEDIA_SINGLE_CHAT + text]: 5,
                                        }));
                                    },
                                });
                            }}
                        />
                    </FlexRowAlignCenter>
                )}
            </FlexColumn>
        </Flex>
    );
};

export default ChatText;
